import React, { useEffect, useState } from 'react';
import { Checkbox, Spin, Table } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import parkService from '../../../services/config/park.service';

const TicketApplicableUserSelectionForAddOnSetup = ({ 
    ticketType,
    selectedAddOnId,
    addOnSetupId = null,
    onChange = () => {} 
}) => {
    const [state, setState] = useState({
        loading: false,
        ticket_applicable_users: [],
        selected_ticket_user_ids: [],
        selected_add_on: null,
    });

    // Fetch the selected add-on details to check if it's insurance
    const fetchAddOnDetails = async () => {
        if (!selectedAddOnId) return null;

        try {
            const response = await parkService?.getAddOnById(selectedAddOnId);
            const { add_on } = response;
            return add_on;
        } catch (error) {
            console.error('Error fetching add-on details:', error);
            return null;
        }
    };

    // Fetch ticket applicable users for the ticket type
    const fetchTicketApplicableUsers = async () => {
        const { park_applicable_ticket_type_id } = ticketType?.ParkApplicableTicketType || {};
        
        if (!park_applicable_ticket_type_id) return;

        setState(state => ({ ...state, loading: true }));
        
        try {
            const response = await parkService?.getTicketApplicableUsersForAddOn({
                park_applicable_ticket_type_id: park_applicable_ticket_type_id,
                add_on_setup_id: addOnSetupId,
            });

            const { ticket_applicable_users = [], selected_ids = [] } = response;

            setState(state => ({
                ...state,
                loading: false,
                ticket_applicable_users: ticket_applicable_users,
                selected_ticket_user_ids: selected_ids,
            }));

            // Notify parent of initial selection
            onChange(selected_ids);
        } catch (err) {
            console.error('Error fetching ticket applicable users:', err);
            setState(state => ({ ...state, loading: false }));
        }
    };

    // Handle checkbox change
    const onCheckboxChange = (ticket_applicable_user_id, checked) => {
        let new_selected = [...state.selected_ticket_user_ids];

        if (checked) {
            if (!new_selected.includes(ticket_applicable_user_id)) {
                new_selected.push(ticket_applicable_user_id);
            }
        } else {
            new_selected = new_selected.filter(id => id !== ticket_applicable_user_id);
        }

        setState(state => ({
            ...state,
            selected_ticket_user_ids: new_selected,
        }));

        onChange(new_selected);
    };

    // Check if selected add-on is insurance
    const checkIfInsuranceAndFetchTickets = async () => {
        if (!selectedAddOnId) {
            setState(state => ({
                ...state,
                selected_add_on: null,
                ticket_applicable_users: [],
            }));
            return;
        }

        const add_on = await fetchAddOnDetails();
        setState(state => ({ ...state, selected_add_on: add_on }));

        // Check if add_on_section is insurance
        const is_insurance = add_on?.add_on_section?.is_insurance;
        
        if (is_insurance && ticketType?.ParkApplicableTicketType?.park_applicable_ticket_type_id) {
            await fetchTicketApplicableUsers();
        } else {
            // Clear ticket selection if not insurance
            setState(state => ({
                ...state,
                ticket_applicable_users: [],
                selected_ticket_user_ids: [],
            }));
            onChange([]);
        }
    };

    useEffect(() => {
        checkIfInsuranceAndFetchTickets();
    }, [selectedAddOnId, ticketType, addOnSetupId]);

    // Don't render if not insurance or no add-on selected
    const is_insurance = state.selected_add_on?.add_on_section?.is_insurance;
    if (!is_insurance || state.ticket_applicable_users.length === 0) {
        return null;
    }

    const columns = [
        {
            title: 'Ticket Name',
            dataIndex: 'ticket_user_type',
            key: 'ticket_user_type',
            render: (ticket_user_type, row) => {
                const { name, descriptions, min_age, max_age } = ticket_user_type || {};
                return (
                    <div>
                        <div>
                            <span style={{ fontWeight: 500 }}>{name}</span>
                        </div>
                        {descriptions && (
                            <div>
                                <span style={{ fontSize: 12, color: 'var(--secondary-text-color)' }}>
                                    {descriptions}
                                </span>
                            </div>
                        )}
                        {(min_age !== null && min_age !== undefined) || (max_age !== null && max_age !== undefined) ? (
                            <div>
                                <span style={{ fontSize: 12, color: 'var(--secondary-text-color)' }}>
                                    Age: {min_age || 0} - {max_age || 'N/A'}
                                </span>
                            </div>
                        ) : null}
                    </div>
                );
            },
        },
        {
            title: 'Applicable',
            dataIndex: 'ticket_applicable_user_id',
            key: 'applicable',
            width: 120,
            align: 'center',
            render: (ticket_applicable_user_id) => {
                const isChecked = state.selected_ticket_user_ids.includes(ticket_applicable_user_id);
                return (
                    <Checkbox
                        checked={isChecked}
                        onChange={(e) => onCheckboxChange(ticket_applicable_user_id, e.target.checked)}
                    />
                );
            },
        },
    ];

    return (
        <Spin spinning={state.loading} indicator={<LoadingOutlined />}>
            <div style={{ marginTop: 16 }}>
                <div style={{ 
                    marginBottom: 12, 
                    padding: 12, 
                    background: '#e6f7ff', 
                    border: '1px solid #91d5ff',
                    borderRadius: 4,
                }}>
                    <span style={{ fontSize: 14, color: '#0050b3' }}>
                        ℹ️ Insurance add-on detected! Please select which tickets are applicable for this add-on.
                    </span>
                </div>
                <div style={{ marginBottom: 8 }}>
                    <span style={{ fontWeight: 500 }}>Select Applicable Tickets</span>
                </div>
                <Table
                    className='hub-table bordered'
                    dataSource={state.ticket_applicable_users}
                    columns={columns}
                    rowKey="ticket_applicable_user_id"
                    pagination={false}
                    scroll={{ y: 300 }}
                    size="small"
                />
            </div>
        </Spin>
    );
};

export default TicketApplicableUserSelectionForAddOnSetup;